import React, { useState, useEffect, ChangeEvent } from 'react';
import { Modal, Form, Button } from 'react-bootstrap';
import axios from '../../../api/axios';
import { Admin } from '../../../types';
import { validateEmail, containsEmoji } from '../../../utils/Utils';
import { ApiError } from '../../../types/errors';

interface AdminDetailsModalProps {
  show: boolean;
  onHide: () => void;
  admin: Admin | null;
}

interface AdminData {
  name: string;
  email: string;
}

interface AdminErrors {
  name?: string;
  email?: string;
  submit?: string;
}

interface AdminUpdateEvent extends CustomEvent {
  detail: {
    name: string;
    email: string;
  };
}

const AdminDetailsModal: React.FC<AdminDetailsModalProps> = ({ show, onHide, admin }) => {
  const [adminData, setAdminData] = useState<AdminData>({
    name: '',
    email: ''
  });
  const [name, setName] = useState<string>('');
  const [email, setEmail] = useState<string>('');
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [errors, setErrors] = useState<AdminErrors>({});

  useEffect(() => {
    if (show && admin) {
      setAdminData({
        name: admin.name || '',
        email: admin.email || ''
      });
      setName(admin.name || '');
      setEmail(admin.email || '');
      setIsEditing(false);
      setErrors({});
    }
  }, [show, admin]);

  const handleChange = (e: ChangeEvent<HTMLInputElement>): void => {
    const { name, value } = e.target;
    if (name === 'name') setName(value);
    if (name === 'email') setEmail(value);
  };

  const validateForm = (): AdminErrors => {
    const newErrors: AdminErrors = {};

    if (!name.trim()) newErrors.name = '管理者名を入力してください。';
    else if (containsEmoji(name)) newErrors.name = '絵文字は使用できません。';

    if (!email.trim()) newErrors.email = 'メールアドレスを入力してください。';
    else if (!validateEmail(email)) newErrors.email = '不正なメールアドレスです。';
    else if (containsEmoji(email)) newErrors.email = '絵文字は使用できません。';

    return newErrors;
  };

  const handleSubmit = async (): Promise<void> => {
    const newErrors = validateForm();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    try {
      const token = localStorage.getItem('admin_token');
      const updatedAdmin = { name: name, email: email };
      await axios.put('/admin/profile', updatedAdmin, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setAdminData(updatedAdmin);
      setIsEditing(false);
      setErrors({});

      const adminData = JSON.parse(localStorage.getItem('admin') || '{}');
      localStorage.setItem('admin', JSON.stringify({
        ...adminData,
        name: name,
        email: email
      }));

      const updateEvent = new CustomEvent('adminUpdated', { 
        detail: updatedAdmin 
      }) as AdminUpdateEvent;
      window.dispatchEvent(updateEvent);

      onHide();

      alert('管理者情報が更新されました');
    } catch (error: unknown) {
      console.error('Error updating admin details:', error);
      const axiosError = error as ApiError;
      if (axiosError.response?.data?.errors) {
        setErrors({
          ...axiosError.response.data.errors,
        });
      } else if (axiosError.response?.data?.message) {
        alert(axiosError.response.data.message);
      } else {
        alert('更新中にエラーが発生しました。もう一度お試しください。');
      }
    }
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      size='sm'
      centered
      backdrop="static"  // Prevent close when clicking outside
      keyboard={false}   // Optional: Prevent closing with ESC key
    >
      <Modal.Header closeButton>
        <Modal.Title>管理者詳細</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {!isEditing && (
          <div>
            <div className="candidate-detail-item">
              <div className="candidate-detail-label">管理者名</div>
              <div className="candidate-detail-value">{adminData.name}</div>
            </div>
            <div className="candidate-detail-item">
              <div className="candidate-detail-label">メールアドレス</div>
              <div className="candidate-detail-value">{adminData.email}</div>
            </div>
          </div>
        )}
        {isEditing && (
          <div className='candidate-edit-panel'>
            <Form>
            <div className="candidate-detail-item">
              <div className="candidate-detail-label">管理者名 <span className="required-mark">*</span></div>
              <div className="candidate-detail-value">
                <Form.Control
                  type="text"
                  name="name"
                  value={name}
                  onChange={handleChange}
                  disabled={!isEditing}
                  isInvalid={!!errors.name}
                  placeholder="管理者名"
                />
                {errors.name && (
                  <Form.Control.Feedback type="invalid">
                    {errors.name}
                  </Form.Control.Feedback>
                )}
              </div>
            </div>
            <div className="candidate-detail-item">
              <div className="candidate-detail-label">メールアドレス <span className="required-mark">*</span></div>
              <div className="candidate-detail-value">
                <Form.Control
                  type="text"
                  name="email"
                  value={email}
                  onChange={handleChange}
                  disabled={!isEditing}
                  isInvalid={!!errors.email}
                  placeholder="メールアドレス"
                />
                {errors.email && (
                  <Form.Control.Feedback type="invalid">
                    {errors.email}
                  </Form.Control.Feedback>
                )}
              </div>
            </div>
            </Form>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        {isEditing ? (
          <div className="d-flex justify-content-center w-100">
            <Button variant="success" className="me-3" onClick={handleSubmit}>
              保存
            </Button>
            <Button variant="secondary" onClick={() => {
              setIsEditing(false);
              setErrors({});
            }}>
              キャンセル
            </Button>
          </div>
        ) : (
          <Button variant="success" onClick={() => {
            setIsEditing(true);
            setName(adminData.name);
            setEmail(adminData.email);
          }}>
            編集
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default AdminDetailsModal;
